import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { HiX } from 'react-icons/hi';
import classNames from 'classnames';

interface ModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
  className?: string;
  showClose?: boolean;
  zIndex?: string;
}

export default function Modal({
  open,
  setOpen,
  title,
  children,
  footer,
  maxWidth = 'max-w-2xl',
  className,
  showClose = true,
  zIndex = 'z-50',
}: ModalProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className={classNames('relative', zIndex)} onClose={() => setOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel
                className={classNames(
                  'w-full transform overflow-hidden rounded-2xl bg-secondary p-6 text-left align-middle shadow-xl transition-all',
                  maxWidth,
                )}>
                {(title || showClose) && (
                  <div className="mb-6 flex items-center justify-between">
                    {title ? (
                      <Dialog.Title as="h3" className="text-2xl font-semibold text-primary">
                        {title}
                      </Dialog.Title>
                    ) : (
                      <div />
                    )}

                    {showClose && (
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="hover:bg-secondary-100 rounded-lg p-1 transition-colors">
                        <HiX className="h-6 w-6 text-details" />
                      </button>
                    )}
                  </div>
                )}

                <div className={className}>{children}</div>

                {footer && <div className="mt-4">{footer}</div>}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
